import { useState, useRef, useEffect, useCallback } from "react";
import axios from "axios";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { MapPin, Search, Loader2, Package } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

/**
 * ShelfLocationLookupModal — quick-glance utility for the cashier to find
 * out which shelf/bin a product lives on, by PLU, barcode, or description.
 *
 * Pure read-only: does NOT add anything to the cart, does NOT touch stock.
 */
export const ShelfLocationLookupModal = ({ open, onClose }) => {
  const [query, setQuery] = useState("");
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [touched, setTouched] = useState(false);
  const debounceRef = useRef(null);
  const inputRef = useRef(null);

  // Reset and focus on open
  useEffect(() => {
    if (open) {
      setQuery("");
      setResults([]);
      setTouched(false);
      // small timeout so the dialog has mounted
      setTimeout(() => inputRef.current?.focus(), 100);
    }
  }, [open]);

  const runSearch = useCallback((q) => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(async () => {
      const trimmed = (q || "").trim();
      if (trimmed.length < 2) {
        setResults([]);
        setLoading(false);
        return;
      }
      setLoading(true);
      try {
        const res = await axios.get(`${API}/products/search`, {
          params: { q: trimmed },
        });
        setResults(res.data || []);
      } catch (e) {
        setResults([]);
      } finally {
        setLoading(false);
      }
    }, 220);
  }, []);

  const handleChange = (e) => {
    const v = e.target.value;
    setQuery(v);
    setTouched(true);
    runSearch(v);
  };

  const fmtLocation = (p) => {
    // Prefer the new unified `location` field; fall back to legacy
    // shelf_number / bin_number combination so older imports still display.
    const loc = (p.location || "").trim();
    if (loc) return loc;
    const shelf = (p.shelf_number || "").trim();
    const bin = (p.bin_number || "").trim();
    if (shelf && bin) return `Shelf ${shelf} · Bin ${bin}`;
    if (shelf) return `Shelf ${shelf}`;
    if (bin) return `Bin ${bin}`;
    return null;
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent
        className="max-w-2xl max-h-[85vh] overflow-y-auto"
        data-testid="shelf-location-modal"
      >
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <MapPin className="h-5 w-5 text-amber-600" />
            Check Shelf Location
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-3">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
            <Input
              ref={inputRef}
              value={query}
              onChange={handleChange}
              placeholder="Scan barcode, type PLU code, or product description…"
              className="pl-9 text-base h-11"
              data-testid="shelf-lookup-input"
              autoComplete="off"
            />
          </div>

          {!touched && (
            <div className="py-8 text-center text-gray-500 text-sm">
              <Package className="h-10 w-10 mx-auto mb-2 text-gray-300" />
              Start typing or scan a product to see its shelf &amp; bin location.
            </div>
          )}

          {touched && loading && (
            <div className="py-8 text-center text-gray-500 text-sm">
              <Loader2 className="h-6 w-6 mx-auto mb-2 animate-spin" />
              Searching…
            </div>
          )}

          {touched && !loading && results.length === 0 && query.trim().length >= 2 && (
            <div className="py-8 text-center text-gray-500 text-sm">
              No products match &ldquo;{query}&rdquo;.
            </div>
          )}

          {!loading && results.length > 0 && (
            <div className="space-y-2" data-testid="shelf-lookup-results">
              {results.slice(0, 20).map((p) => {
                const loc = fmtLocation(p);
                return (
                  <div
                    key={p.id}
                    className="border rounded-lg p-3 bg-white hover:bg-amber-50 transition-colors"
                    data-testid={`shelf-result-${p.id}`}
                  >
                    <div className="flex items-start justify-between gap-3">
                      <div className="min-w-0 flex-1">
                        <div className="font-semibold text-sm uppercase truncate">
                          {p.name}
                        </div>
                        <div className="text-xs text-gray-500 mt-0.5 flex flex-wrap gap-x-3 gap-y-0.5">
                          {p.plu_code && <span>PLU: <b>{p.plu_code}</b></span>}
                          {p.barcode && <span>Barcode: {p.barcode}</span>}
                          {typeof p.stock_qty === "number" && (
                            <span>Stock: {p.stock_qty}</span>
                          )}
                        </div>
                      </div>
                      <div className="text-right flex-shrink-0">
                        {loc ? (
                          <div
                            className="inline-flex items-center gap-1 px-3 py-1.5 rounded-md bg-amber-100 text-amber-900 font-bold text-sm border border-amber-300"
                            data-testid={`shelf-location-${p.id}`}
                          >
                            <MapPin className="h-4 w-4" />
                            {loc}
                          </div>
                        ) : (
                          <div className="text-xs italic text-gray-400">
                            No location set
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                );
              })}
              {results.length > 20 && (
                <div className="text-xs text-gray-500 text-center py-1">
                  Showing first 20 of {results.length} matches — refine your search.
                </div>
              )}
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default ShelfLocationLookupModal;
